import { Alert, Flex, Button } from "@hcsc-fiber/react";
import { useFeatureFlag } from "@mma/common";
import axios from "axios";
import classNames from "classnames";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { UAParser } from "ua-parser-js";
import {
  accessTokenFetchFailed,
  defaultTrustedDeviceNameFromCIDM,
  passKeyAddorDeleteFail,
  passKeyFetchFail,
  trustedDevicesDeleteFail,
  trustedDevicesFetchFail,
} from "../../constants/static";
import { devicesUrl } from "../../constants/urls";
import { StateContext } from "../../context/stateContext";
import { getAccessToken } from "../../endpoints/acessToken";
import useConfig from "../../hooks/useConfig";
import styles from "../../styles/third-party.module.scss";
import { translations } from "../../translate";
import { Loader } from "../Loader/Loader";
import { PassKeyAdd } from "../PassKey/AddPasskey";
import { PassKeyListAndAdd } from "../PassKey/PassKey";
import { RemoveDeviceDialog } from "../RemoveDeviceDialog/RemoveDeviceDialog";
import { ErrorLayout } from "./../ErrorLayout/ErrorLayout";
import { DeviceCard } from "./DeviceCard";
import { Device, DevicesStepResponse, parsedDevice } from "./TrustedDevice.type";
import trustedDeviceStyles from "./TrustedDevices.module.scss";

export const TrustedDevices = () => {
  const [devicesStep1Response, setDevicesStep1Response] = useState<DevicesStepResponse>();
  const [devicesStep2Response, setDevicesStep2Response] = useState<DevicesStepResponse>();
  const [devicesStep3Response, setDevicesStep3Response] = useState<DevicesStepResponse>();
  const [devicesStep4Response, setDevicesStep4Response] = useState<DevicesStepResponse>();
  const [allDevices, setAllDevices] = useState<Device[]>([]);
  const [activeDevices, setActiveDevices] = useState<Device[]>([]);
  const [expiredDevices, setExpiredDevices] = useState<Device[]>([]);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deviceToRemove, setDeviceToRemove] = useState<string>("");
  const [dialogDeviceMessage, setDialogDeviceMessage] = useState("");
  const [deviceDeleteMessage, setDeviceDeleteMessage] = useState("");
  const {
    cidmUrl,
    accessToken,
    setAccessToken,
    setIsError,
    isError,
    isAuthorizeError,
    languageCode,
    authorizationCode,
    stateUSACode,
    passKeyCount,
    setRemoveDeviceSuccess,
    removeDeviceSuccess,
    setLastRemovedDeviceMessage,
    lastRemovedDeviceMessage,
    setAddPassKeySuccess,
    setAddedPassKeyMessage,
    addedPassKeyMessage,
    addPassKeySuccess,
  } = useContext(StateContext);
  const oneDayInMilliSeconds = 86400000;
  const environmentVars = useConfig();
  const showDialog = (device: Device) => {
    setIsDialogOpen(true);
    setDeviceToRemove(device.uuid);
    if (device.deviceName != "" && device.deviceName != defaultTrustedDeviceNameFromCIDM) {
      setDialogDeviceMessage(device.deviceName);
      setLastRemovedDeviceMessage(device.deviceName);
      setDeviceDeleteMessage(device.deviceName);
    } else {
      const vendor = device.deviceInfo.device.vendor || "";
      const model = device.deviceInfo.device.model || "";
      const osName = device.deviceInfo.os?.name || "";
      const showOS = !vendor && !model;
      const deviceMessage = `${vendor} ${model} 
                                   ${showOS ? osName : ""}
                                   ${
                                     device.deviceInfo.browser.name
                                       ? " - " + device.deviceInfo.browser.name + " Browser"
                                       : ""
                                   }`;
      setDialogDeviceMessage(deviceMessage);
      setLastRemovedDeviceMessage(deviceMessage);
      setDeviceDeleteMessage(deviceMessage);
    }
  };

  const onRemoveDevice = () => {
    setIsDialogOpen(false);
    setIsDeleting(true);
    setIsLoading(true);
    fetchData(deviceBackendUrl, "", config, setDevicesStep1Response, trustedDevicesDeleteFail);
  };

  const onCloseDialog = () => {
    setIsDialogOpen(false);
    setDeviceToRemove("");
    setLastRemovedDeviceMessage("");
  };

  const onCloseAlert = () => {
    setLastRemovedDeviceMessage("");
    setRemoveDeviceSuccess(false);
    setAddPassKeySuccess(false);
    setAddedPassKeyMessage("");
  };

  const deviceBackendUrl = cidmUrl + devicesUrl;
  const expiredDays = Number(useFeatureFlag("Trusted_Devices_Expired_Days", true));

  //fetch access token
  useEffect(() => {
    if (!cidmUrl || isAuthorizeError) {
      return;
    }
    getAccessToken(cidmUrl, authorizationCode)
      .then(tokenResponse => {
        setAccessToken(tokenResponse.data.access_token);
      })
      .catch(() => {
        setIsError(accessTokenFetchFailed);
      });
  }, [cidmUrl, setAccessToken]);

  //function to fetch CIDM data
  const fetchData = (
    url: string,
    data: any,
    config: any,
    setResponse: React.Dispatch<React.SetStateAction<DevicesStepResponse | undefined>>,
    errorType: string
  ) => {
    axios
      .post<DevicesStepResponse>(url, data, config)
      .then(data => {
        setResponse(data.data);
      })
      .catch(error => {
        setIsError(errorType);
        setIsLoading(false);
      });
  };

  //config object to send in cidm calls
  const config = useMemo(() => {
    return {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${accessToken}`,
        "accept-api-version": "protocol=1.0,resource=2.1",
        "Content-Type": "application/json",
      },
      withCredentials: true,
      params: {
        authIndexType: "composite_advice",
        authIndexValue:
          "<Advices><AttributeValuePair><Attribute name=\"AuthenticateToServiceConditionAdvice\"/><Value>/members:HCSC_Manage_Trusted_Device</Value></AttributeValuePair></Advices>",
      },
    };
  }, [accessToken]);

  // initial cidm call to fetch list of devices on pageload - Step 1
  useEffect(() => {
    if (accessToken && cidmUrl) {
      setIsLoading(true);
      fetchData(deviceBackendUrl, "", config, setDevicesStep1Response, trustedDevicesFetchFail);
    }
  }, [cidmUrl, accessToken]);

  //Step 2
  useEffect(() => {
    if (
      !devicesStep1Response ||
      devicesStep1Response?.callbacks == undefined ||
      !(devicesStep1Response?.callbacks[0]?.output[0]?.value === "TrustedDeviceManagement")
    )
      return;
    if (!isDeleting) {
      fetchData(deviceBackendUrl, devicesStep1Response, config, setDevicesStep2Response, trustedDevicesFetchFail);
    } else {
      const callback = devicesStep1Response.callbacks;
      callback[0].input[0].value = 1;
      fetchData(
        deviceBackendUrl,
        {
          ...devicesStep1Response,
          callbacks: callback,
        },
        config,
        setDevicesStep3Response,
        trustedDevicesDeleteFail
      );
    }
  }, [devicesStep1Response]);

  //step 3
  useEffect(() => {
    if (!devicesStep2Response) return;
    //saving fetched devices in state
    if (!isDeleting) {
      const parsedArray: Device[] = [];
      const parsedActiveDevicesArray: Device[] = [];
      const parsedExpiredDevicesArray: Device[] = [];
      const devicesArray = devicesStep2Response.callbacks[0]?.output[1]?.value;
      const parser = new UAParser();

      if (devicesStep2Response.callbacks[0]?.output[0]?.value === "Device List" && Array.isArray(devicesArray)) {
        devicesArray
          .filter(e => !!e.trim())
          .map(value => {
            const parsedValue: parsedDevice = JSON.parse(value);
            parser.setUA(parsedValue.userAgent);
            const deviceData = {
              deviceInfo: parser.getResult(),
              lastSelectedDate: parsedValue.lastSelectedDate,
              uuid: parsedValue.uuid,
              deviceName: parsedValue.DeviceName,
              value: value,
            };
            parsedArray.push(deviceData);
            if (parsedValue.lastSelectedDate != "" && parsedValue.lastSelectedDate != undefined) {
              const dateDiff = (Date.now() - parseInt(parsedValue.lastSelectedDate)) / oneDayInMilliSeconds;
              if (dateDiff > expiredDays) {
                parsedExpiredDevicesArray.push(deviceData);
              } else {
                parsedActiveDevicesArray.push(deviceData);
              }
            } else {
              parsedExpiredDevicesArray.push(deviceData);
            }
          });

        setAllDevices(parsedArray);
        setActiveDevices(
          parsedActiveDevicesArray.sort(
            (a: Device, b: Device) => parseInt(b.lastSelectedDate) - parseInt(a.lastSelectedDate)
          )
        );
        setExpiredDevices(
          parsedExpiredDevicesArray.sort(
            (a: Device, b: Device) => parseInt(b.lastSelectedDate) - parseInt(a.lastSelectedDate)
          )
        );
      } else if (
        devicesStep2Response.callbacks[0]?.output[0]?.value === "Device List" &&
        Array.isArray(devicesArray) &&
        devicesArray[0] == " "
      ) {
        setAllDevices([]);
      } else {
        setIsError(trustedDevicesFetchFail);
      }
      setIsLoading(false);
    }
  }, [devicesStep2Response, expiredDays]);

  //step 3 - delete
  useEffect(() => {
    if (!devicesStep3Response) return;
    else {
      let devicesArray;
      if (
        devicesStep3Response.callbacks[0]?.output[0]?.value === "enter device Id" &&
        Array.isArray((devicesArray = devicesStep3Response.callbacks[0]?.output[1]?.value))
      ) {
        const deleteDeviceIndex = devicesArray.findIndex((value: string) => {
          const parsedValue: parsedDevice = JSON.parse(value);
          return parsedValue.uuid === deviceToRemove;
        });
        if (deleteDeviceIndex > -1) {
          const callback = devicesStep3Response.callbacks;
          callback[0].input[0].value = deleteDeviceIndex;
          fetchData(
            deviceBackendUrl,
            {
              ...devicesStep3Response,
              callbacks: callback,
            },
            config,
            setDevicesStep4Response,
            trustedDevicesDeleteFail
          );
        }
      } else {
        setIsError(trustedDevicesDeleteFail);
        setIsLoading(false);
      }
    }
  }, [devicesStep3Response]);

  //step 4 - delete step 3 response
  useEffect(() => {
    if (!devicesStep4Response) return;
    else if (devicesStep4Response?.callbacks[0]?.output[0]?.value === "Sucess! Do you want to continue?") {
      setRemoveDeviceSuccess(true);
      setIsDeleting(false);
      setDeviceToRemove("");
      // setDeviceDeleteMessage(deviceMessage);
      setLastRemovedDeviceMessage(deviceDeleteMessage);
      //fetching new device list again
      fetchData(deviceBackendUrl, "", config, setDevicesStep1Response, trustedDevicesFetchFail);
    }
  }, [devicesStep4Response]);

  if (isAuthorizeError) {
    return (
      <div className={classNames(styles["right-pane"], styles.errorWrapper)}>
        <ErrorLayout
          // header="" TODO: ASK ALLYSSA FOR ERROR MESSAGE after API will be ready
          header=""
          subheader={translations.sorryMessage[languageCode]}
        />
      </div>
    );
  }
  const handleCloseAlertState = () => {
    onCloseAlert();
    setIsError("");
  };

  if (isLoading) {
    return (
      <div style={{ marginTop: "2rem" }}>
        <Loader size={"medium"} />
      </div>
    );
  }
  const navigateToMyBam = () => {
    // TODO: CHANGE BUTTON BACK TO LINK AFTER UPDATE FIBER ,AND LINK WILL RESPECT 'TARGET' PROPERTY
    const myBamUrl = environmentVars?.MYBAM_URL?.replace("bcbsil", `bcbs${stateUSACode}`);
    window.location.assign(myBamUrl);
  };

  const getErrorMessage = (isError: string) => {
    let errorMessage = translations.failedToFetch[languageCode];
    if (isError === trustedDevicesDeleteFail || passKeyAddorDeleteFail) {
      errorMessage = translations.failedToRemove[languageCode];
    }
    if (isError === passKeyFetchFail) {
      errorMessage = translations.failedToFetchPasskey[languageCode];
    }
    return errorMessage;
  };

  return (
    <div className={classNames(styles["right-pane"], trustedDeviceStyles.rightSide)}>
      {/*<p className={styles.header}>{translations.securityCenter[languageCode]}</p>*/}
      <div style={{ width: "100%" }}>
        {2 + 2 === 5 && (
          <Flex justifyContent="end" className={trustedDeviceStyles.logIntoBamButton} margin={[0, 0, 1, 0]}>
            <Button kind="secondary" onClick={navigateToMyBam}>
              {translations.loginToMemberPortal[languageCode]}
            </Button>
          </Flex>
        )}
        <h1 className={styles["right-pane__title"]}>{translations.securityCenter[languageCode]}</h1>
        <p className={styles["right-pane__paragraph"]}>{translations.securityCenterDescription[languageCode]}</p>
      </div>
      {isDialogOpen && (
        <RemoveDeviceDialog
          dialogDeviceMessage={dialogDeviceMessage}
          isOpen={isDialogOpen}
          onClose={onCloseDialog}
          onSubmit={onRemoveDevice}
          type="device"
        />
      )}
      {(removeDeviceSuccess || addPassKeySuccess) && (
        <div>
          <Alert kind="success" onDismiss={onCloseAlert}>
            {addPassKeySuccess && addedPassKeyMessage
              ? `${translations.addPasskeySuccessMessage[languageCode]} ${addedPassKeyMessage}`
              : `${translations.removeSuccessMessage[languageCode]} ${lastRemovedDeviceMessage}`}
          </Alert>
        </div>
      )}
      {isError.length > 0 && (
        <div>
          <Alert kind="danger" onDismiss={() => setIsError("")}>
            {getErrorMessage(isError)}
          </Alert>
        </div>
      )}
      {isError != trustedDevicesFetchFail && (
        <div>
          {/*Active Devices*/}
          <div>
            <p className={styles["right-pane__paragraph"]}>
              <strong>
                {translations.devices[languageCode]} ({activeDevices.length + passKeyCount})
              </strong>
            </p>
            {activeDevices.map(device => (
              <DeviceCard onRemove={showDialog} device={device} key={device.uuid} closeAlert={handleCloseAlertState} />
            ))}
            <PassKeyListAndAdd closeAlert={handleCloseAlertState} />
          </div>
          {/*Expired Devices*/}
          <div>
            <p className={styles["right-pane__paragraph"]}>
              <strong>
                {translations.expireDevices[languageCode]} ({expiredDevices.length > 5 ? 5 : expiredDevices.length})
              </strong>
            </p>
            <p className={styles["right-pane__paragraph"]} style={{ marginTop: "0rem" }}>
              {translations.expiredDevicesDescription[languageCode]}
            </p>
            {expiredDevices
              .filter((value, index) => index < 5)
              .map(device => (
                <DeviceCard
                  onRemove={showDialog}
                  device={device}
                  key={device.uuid}
                  closeAlert={handleCloseAlertState}
                />
              ))}
          </div>
        </div>
      )}
      <PassKeyAdd closeAlert={handleCloseAlertState} />
    </div>
  );
};
